import { useState, useEffect } from 'react';
import { X, Save, RotateCcw } from 'lucide-react';
import { TenantModuleStatus } from '../../types/modules';

interface ModuleConfigModalProps {
  module: TenantModuleStatus | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (moduleId: string, config: Record<string, any>) => Promise<void>;
  isLoading?: boolean;
}

export function ModuleConfigModal({
  module,
  isOpen,
  onClose,
  onSave,
  isLoading = false,
}: ModuleConfigModalProps) {
  const [config, setConfig] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (module) {
      setConfig(module.config || module.defaultConfig || {});
    }
  }, [module]);

  if (!isOpen || !module) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(module.id, config);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(module.defaultConfig || {});
  };

  const updateNestedConfig = (path: string[], value: any) => {
    setConfig((prev) => {
      const newConfig = { ...prev };
      let current: any = newConfig;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current[path[i]] = { ...current[path[i]] };
        current = current[path[i]];
      }
      
      current[path[path.length - 1]] = value;
      return newConfig;
    });
  };

  const renderConfigField = (key: string, value: any, path: string[] = []) => {
    const currentPath = [...path, key];
    const fieldId = currentPath.join('.');

    if (typeof value === 'boolean') {
      return (
        <div key={fieldId} className="flex items-center justify-between py-2">
          <label htmlFor={fieldId} className="text-sm text-gray-700 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <button
            type="button"
            onClick={() => updateNestedConfig(currentPath, !value)}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${value ? 'bg-blue-500' : 'bg-gray-300'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${value ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      );
    }

    if (typeof value === 'number') {
      return (
        <div key={fieldId} className="py-2">
          <label htmlFor={fieldId} className="block text-sm text-gray-700 capitalize mb-1">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <input
            id={fieldId}
            type="number"
            value={value}
            onChange={(e) => updateNestedConfig(currentPath, Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      );
    }

    if (typeof value === 'string') {
      return (
        <div key={fieldId} className="py-2">
          <label htmlFor={fieldId} className="block text-sm text-gray-700 capitalize mb-1">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => updateNestedConfig(currentPath, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div key={fieldId} className="py-2">
          <label htmlFor={fieldId} className="block text-sm text-gray-700 capitalize mb-1">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value.join(', ')}
            onChange={(e) => updateNestedConfig(currentPath, e.target.value.split(',').map(s => s.trim()))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Valores separados por vírgula"
          />
        </div>
      );
    }

    if (typeof value === 'object' && value !== null) {
      return (
        <div key={fieldId} className="py-2">
          <h4 className="text-sm font-medium text-gray-800 capitalize mb-2 border-b pb-1">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </h4>
          <div className="pl-4 border-l-2 border-gray-200">
            {Object.entries(value).map(([k, v]) => renderConfigField(k, v, currentPath))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Configurar {module.name}
              </h2>
              <p className="text-sm text-gray-500">{module.description}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            {Object.entries(config).map(([key, value]) => renderConfigField(key, value))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              <RotateCcw className="w-4 h-4" />
              Restaurar Padrão
            </button>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModuleConfigModal;
